import type { BadgeProps } from "@marekl/adminer/components";
import { formatDuration } from "@marekl/adminer/format";
import type { StartListBlockKind, StartListLaneMode, StartListStage } from "./queries/startlists";

/**
 * Czech labels for the start-list enums (Modul 7), in one place for the
 * same reason as competitionState.ts: every surface has to word them
 * identically, and the audit log, the page and the print view all read
 * from here rather than inlining a string.
 */

export const BLOCK_KIND_LABELS: Record<StartListBlockKind, string> = {
  presence: "Prezence",
  warmup: "Prostorová zkouška",
  briefing: "Porada vedoucích",
  break: "Přestávka",
  lunch: "Obědová pauza",
  ceremony: "Vyhlášení",
  other: "Jiné",
};

/** Ordered for a picker; "Jiné" last because it is the fallback. */
export const BLOCK_KIND_OPTIONS: StartListBlockKind[] = [
  "presence",
  "warmup",
  "briefing",
  "break",
  "lunch",
  "ceremony",
  "other",
];

/**
 * Which blocks run ALONGSIDE the running order rather than between two
 * performances. Prezence and prostorové zkoušky do — they occupy a window
 * while competitors are already performing — so the form opens with the
 * right answer instead of making the organizer discover the distinction.
 */
export const BLOCK_PARALLEL_BY_DEFAULT = new Set<StartListBlockKind>(["presence", "warmup"]);

/** Sensible starting lengths, in minutes, so the form opens usable. */
export const BLOCK_DEFAULT_MINUTES: Record<StartListBlockKind, number> = {
  presence: 60,
  warmup: 45,
  briefing: 15,
  break: 15,
  lunch: 45,
  ceremony: 20,
  other: 15,
};

/** A skupina drah's modes. Twin of LaneModeLabels in
 * internal/service/startlist/export.go, compared by
 * e2e/specs/startlist/export.spec.ts. */
export const LANE_MODE_LABELS: Record<StartListLaneMode, string> = {
  parallel: "Souběžně",
  alternating: "Střídavě",
};

/** The three stages of a start list (R16), `stage` on the wire. */
export const STAGE_LABELS: Record<StartListStage, string> = {
  draft: "Rozpracovaná",
  preview: "Zveřejněný návrh",
  published: "Zveřejněno",
};

export const STAGE_VARIANT: Record<StartListStage, BadgeProps["variant"]> = {
  draft: "muted",
  preview: "info",
  published: "ok",
};

/** What a zveřejněný návrh says wherever a club or the print reads it. */
export const PREVIEW_NOTICE =
  "Startovní listina je zatím návrh a pořadí se ještě může změnit. Startovní čísla uvidíte po zveřejnění.";

export const PUBLISH_DRAFT_LABEL = "Zveřejnit návrh";
export const EDIT_PUBLISHED_LABEL = "Upravit zveřejněnou listinu";

/**
 * Seconds past midnight as "07:00".
 *
 * Zero-padded to match `formatTime` from the lib, which every other
 * clock time in the app goes through — the rows of a start list are
 * formatted by it, and a summary reading "7:00" above rows reading
 * "07:00" looks like two different quantities.
 */
export function formatDayTime(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** "07:00" → seconds from midnight. Falls back to 0 on nonsense, which
 * the server then rejects with a field error rather than the form
 * silently inventing midnight. */
export function parseDayTime(value: string): number {
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 3600 + m * 60;
}

/**
 * The stopáž window as the rules state it: „1:30–2:45", „max. 2:45",
 * „min. 1:30", or nothing at all when the rule set gives neither bound.
 *
 * An open-ended window is not an error — plenty of Kombinace cap the
 * length without demanding a minimum — so it reads as the one bound that
 * exists rather than inventing a zero for the other.
 */
export function formatWindow(min?: number | null, max?: number | null): string {
  if (min && max) return `${formatDuration(min)}–${formatDuration(max)}`;
  if (max) return `max. ${formatDuration(max)}`;
  if (min) return `min. ${formatDuration(min)}`;
  return "";
}
